import api from "./api";
import { generateErrorMessage } from "../utils/message";

/*Menu Master*/
export const registerMenu = async (menuData) => {
  try {
    const response = await api.post("/menu-master", menuData);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("register", "menu");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const fetchMenusFromAPI = async (page = 1, perPage = 10, search = "", columnFilters = {}, sort = {}) => {
  try {
    // Build params object with column-specific filters
    const params = {
      page,
      per_page: perPage
    };

    // Add search parameter if provided (for backward compatibility)
    if (search) {
      params.search = search;
    }

    // Add column-specific parameters
    if (columnFilters.menu_id) params.menu_id = columnFilters.menu_id;
    if (columnFilters.menu_name) params.menu_name = columnFilters.menu_name;
    if (columnFilters.menu_path) params.menu_path = columnFilters.menu_path;
    if (columnFilters.menu_icon) params.menu_icon = columnFilters.menu_icon;
    if (columnFilters.parent_menu_id) params.parent_menu_id = columnFilters.parent_menu_id;
    if (columnFilters.description) params.description = columnFilters.description;

    if (sort && sort.sortBy) {
      params.sort_by = sort.sortBy;
      params.sort_dir = sort.sortDir === 'desc' ? 'desc' : 'asc';
    }

    const response = await api.get("/menu-master", {
      params: params,
    });

    return response.data.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data", "menu");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

/* Fetch every menu matching the filters, walking server pages (for Export). */
export const fetchAllMenusForExport = async ({ search = "", columnFilters = {}, sort = {} } = {}, onProgress) => {
  const PER_PAGE = 1000;
  const collected = [];
  let page = 1;
  let total = 0;
  for (;;) {
    const response = await fetchMenusFromAPI(page, PER_PAGE, search, columnFilters, sort);
    const payload = response?.menus || {};
    const rows = Array.isArray(payload.data) ? payload.data : [];
    collected.push(...rows);
    total = Number(payload.total) || collected.length;
    if (onProgress) onProgress(collected.length, total);
    const lastPage = Number(payload.last_page) || 1;
    if (rows.length === 0 || page >= lastPage || collected.length >= total) break;
    page += 1;
  }
  return collected;
};

export const deleteMenusFromAPI = async (menu_id) => {
  try {
    const response = await api.delete(`/menu-master/${menu_id}`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("delete data", "menu");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const fetchMenusById = async (menu_id) => {
  try {    
    const response = await api.get(`/menu-master/${menu_id}`);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("get data menu", "menu");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

export const updateMenu = async (menu_id, menuData) => {
  try {
    const response = await api.put(`/menu-master/${menu_id}`, menuData);
    return response.data;
  } catch (error) {
    const fallback = generateErrorMessage("update", "menu");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

// Fetch all menus that can be used as parent (parent_menu_id is null or empty)
export const fetchParentMenus = async () => {
  try {
    const response = await api.get("/menu-master", {
      params: {
        page: 1,
        per_page: 1000, // Get all menus
      },
    });
    
    // Handle different response structures
    // Structure could be: response.data.data.menus.data or response.data.data.menus or response.data.data
    let allMenus = [];
    
    if (response.data?.data?.menus?.data) {
      // Structure: { data: { menus: { data: [...] } } }
      allMenus = response.data.data.menus.data;
    } else if (response.data?.data?.menus) {
      // Structure: { data: { menus: [...] } }
      allMenus = Array.isArray(response.data.data.menus) 
        ? response.data.data.menus 
        : [];
    } else if (response.data?.data) {
      // Structure: { data: [...] }
      allMenus = Array.isArray(response.data.data) 
        ? response.data.data 
        : [];
    } else if (response.data?.menus) {
      // Structure: { menus: [...] }
      allMenus = Array.isArray(response.data.menus) 
        ? response.data.menus 
        : [];
    }
    
    // Filter menus where parent_menu_id is null, empty, or undefined
    const parentMenus = allMenus.filter(menu => {
      const parentId = menu.parent_menu_id;
      return !parentId || parentId === '' || parentId === null || parentId === undefined;
    });
    
    console.log('📋 fetchParentMenus - Total menus:', allMenus.length, 'Parent menus:', parentMenus.length);
    
    return parentMenus;
  } catch (error) {
    console.error('❌ Error fetching parent menus:', error);
    const fallback = generateErrorMessage("get parent menus", "menu");
    const msg = error.response?.data?.message || fallback;
    throw new Error(msg);
  }
};

// Check if menu_id already exists
export const checkMenuIdExists = async (menu_id) => {
  try {
    const response = await api.get("/menu-master", {
      params: {
        menu_id: menu_id,
        per_page: 1,
      },
    });
    
    const menus = response.data.data?.menus || response.data.data || [];
    return menus.length > 0;
  } catch (error) {
    // If error, assume it doesn't exist
    return false;
  }
};

